'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { Heading } from '../../../../components/Heading/Heading';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { getEdgesFromConnection } from '../../../../helpers/graphql';
import { IBoundingBox } from '../../../../interfaces/geo';
import {
  GetPlaceTopSpecies_entry_topSpeciesSummary,
  GetPlaceTopSpecies_entry_topSpeciesSummary_edges,
  GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node,
} from '../../../../interfaces/graphql';
import { PLACE_SUB_LINK_IDS } from '../../helpers/getPlaceSubNavLinks';
import { ITopSpeciesFishingSpots } from '../../Place';
import { TopSpeciesCard } from '../TopSpeciesCard/TopSpeciesCard';
import { TopSpeciesCTA } from '../../../../components/TopSpeciesCTA/TopSpeciesCTA';

interface IProps {
  boundingBox?: IBoundingBox;
  className?: string;
  placeId: string;
  placeName: string;
  topSpeciesSummary?: GetPlaceTopSpecies_entry_topSpeciesSummary;
  topSpeciesFishingSpots: ITopSpeciesFishingSpots[];
}

export const TopSpeciesSection = ({
  boundingBox,
  placeId,
  placeName,
  topSpeciesSummary,
  className,
  topSpeciesFishingSpots,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [visibleTopSpeciesItems, setVisibleTopSpeciesItems] = useState(topSpeciesFishingSpots);
  const [showDownloadCTA, setShowDownloadCTA] = useState(false);
  const { currentPage } = useAnalytics();

  const headingText = `Top fish species in ${placeName}`;
  const speciesEdges = getEdgesFromConnection<
    GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node,
    GetPlaceTopSpecies_entry_topSpeciesSummary_edges
  >(topSpeciesSummary);

  const speciesCount = speciesEdges.length;

  const handleShowMore = () => {
    // First click -> show all species
    if (visibleTopSpeciesItems.length < speciesCount) {
      speciesEdges.map(({ node }, index) => {
        if (index >= visibleTopSpeciesItems.length) {
          setVisibleTopSpeciesItems(prev => [...prev, { speciesId: node._id }]);
        }
        return null;
      });
      // Second click -> show download CTA
    } else {
      setShowDownloadCTA(true);
      return;
    }
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: placeId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleCTAClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: placeId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={PLACE_SUB_LINK_IDS.TOP_SPECIES}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mb-4 sm:mb-8" variant="sm" as="h2">
        {headingText}
      </Heading>
      <div className="flex flex-col">
        {speciesEdges.map(({ node }, index) => {
          const image = node.image;
          const imageUrl = image.url || '';
          const imageWidth = image.width || 0;
          const imageHeight = image.height || 0;

          return (
            <TopSpeciesCard
              boundingBox={index >= visibleTopSpeciesItems.length ? undefined : boundingBox}
              key={`species-${node._id}`}
              speciesId={node._id}
              name={node.displayName}
              className={`w-full ${index >= visibleTopSpeciesItems.length ? 'hidden' : ''}`}
              imageUrl={imageUrl}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              clickEventPosition={index}
              clickEventSourceId={placeId}
              clickEventSource="TopSpeciesSection"
              nrOfFishingSpots={topSpeciesFishingSpots[index]?.nrOfWaters}
            />
          );
        })}
        {showDownloadCTA ? (
          <TopSpeciesCTA onCTAClick={handleCTAClick} sourceId={placeId} />
        ) : (
          <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleShowMore}>
            Show more species
          </Button>
        )}
      </div>
      <AppDownloadModal
        appDownloadSource="TopSpeciesSection"
        appDownloadSourceId={placeName}
        modalHeader={`Download the Fishbrain app to see all species from  ${placeName}`}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
